<script setup lang="ts">
import { DataTable, DateRangeSelector, FilterButtonComponent, formatNumber } from '@metapic-apps/shared'
import { computed, ref, toRef } from 'vue'

import { useAffiliateLinkStats } from '@/api/affiliate-link-stats.api'
import { formatMoneyForAdmin } from '@/lib/formatters/money.formatter'
import { FlexComponent, LoadingComponent, TypographyComponent } from '@/ui-library'

const props = defineProps<{ linkId: number }>()

const dateRange = ref<{ startDate: string; endDate: string } | undefined>(undefined)
const { data: stats, isLoading, error } = useAffiliateLinkStats(toRef(props, 'linkId'), dateRange)

const sourceLabel = (source: string) =>
  source
    .replaceAll('_', ' ')
    .toLowerCase()
    .replace(/^\w/, (letter) => letter.toUpperCase())

const sources = computed(() => {
  if (!stats.value) {
    return []
  }
  return [
    ...new Set([
      ...Object.keys(stats.value.clicks.bySource),
      ...Object.keys(stats.value.costCreator.bySource),
      ...Object.keys(stats.value.costCreatorPending?.bySource ?? {}),
      ...Object.keys(stats.value.orderValue?.bySource ?? {}),
      ...Object.keys(stats.value.estimatedViews?.bySource ?? {}),
      ...Object.keys(stats.value.estimatedUniqueViews?.bySource ?? {}),
      ...Object.keys(stats.value.estimatedMediaValue?.bySource ?? {}),
    ]),
  ].sort((a, b) => a.localeCompare(b))
})

const summary = computed(() => {
  if (!stats.value) {
    return []
  }
  return [
    { label: 'Clicks', value: formatNumber(stats.value.clicks.total, 'standard', 'decimal', 0) },
    { label: 'Creator earnings', value: formatMoneyForAdmin(stats.value.costCreator.total, { allowZero: true }) },
    { label: 'Pending earnings', value: formatMoneyForAdmin(stats.value.costCreatorPending?.total, { allowZero: true }) },
    { label: 'Order value', value: formatMoneyForAdmin(stats.value.orderValue?.total, { allowZero: true }) },
    { label: 'Est. views', value: formatNumber(stats.value.estimatedViews?.total ?? 0, 'standard', 'decimal', 0) },
    { label: 'Est. unique views', value: formatNumber(stats.value.estimatedUniqueViews?.total ?? 0, 'standard', 'decimal', 0) },
    { label: 'Est. media value', value: formatMoneyForAdmin(stats.value.estimatedMediaValue?.total, { allowZero: true }) },
  ]
})
</script>

<template>
  <FlexComponent direction="column" :gap="24" class="breakdown">
    <FlexComponent justify="space-between" align="center" :gap="16" class="breakdown__header">
      <div>
        <TypographyComponent variant="title-large">Performance breakdown</TypographyComponent>
        <TypographyComponent variant="body-medium" class="breakdown__subtitle">All traffic sources</TypographyComponent>
      </div>
      <DateRangeSelector v-model:date-range="dateRange" placement="bottom-end" />
    </FlexComponent>

    <LoadingComponent v-if="isLoading" :size="40" />
    <ElAlert v-else-if="error" :title="error.message" type="error" />
    <template v-else-if="stats">
      <div class="summary-grid">
        <FilterButtonComponent
          v-for="item in summary"
          :key="item.label"
          :value="item.label"
          :is-active="false"
          :is-disabled="true"
          class="summary-metric"
        >
          <template #label>{{ item.label }}</template>
          <template #count>{{ item.value }}</template>
        </FilterButtonComponent>
      </div>

      <div class="source-table-wrap">
        <DataTable class="source-table">
          <template #header>
            <th scope="col">Metric</th>
            <th v-for="source in sources" :key="source" scope="col" class="source-table__metric">{{ sourceLabel(source) }}</th>
          </template>
          <tr v-if="sources.length > 0">
            <td>Clicks</td>
            <td v-for="source in sources" :key="source" class="source-table__metric">
              {{ formatNumber(stats.clicks.bySource[source] ?? 0, 'standard', 'decimal', 0) }}
            </td>
          </tr>
          <tr v-if="sources.length > 0">
            <td>Creator earnings</td>
            <td v-for="source in sources" :key="source" class="source-table__metric">
              {{ formatMoneyForAdmin(stats.costCreator.bySource[source], { allowZero: true }) }}
            </td>
          </tr>
          <tr v-if="sources.length > 0">
            <td>Pending earnings</td>
            <td v-for="source in sources" :key="source" class="source-table__metric">
              {{ formatMoneyForAdmin(stats.costCreatorPending?.bySource[source], { allowZero: true }) }}
            </td>
          </tr>
          <tr v-if="sources.length > 0">
            <td>Order value</td>
            <td v-for="source in sources" :key="source" class="source-table__metric">
              {{ formatMoneyForAdmin(stats.orderValue?.bySource[source], { allowZero: true }) }}
            </td>
          </tr>
          <tr v-if="sources.length > 0">
            <td>Est. views</td>
            <td v-for="source in sources" :key="source" class="source-table__metric">
              {{ formatNumber(stats.estimatedViews?.bySource[source] ?? 0, 'standard', 'decimal', 0) }}
            </td>
          </tr>
          <tr v-if="sources.length > 0">
            <td>Est. unique views</td>
            <td v-for="source in sources" :key="source" class="source-table__metric">
              {{ formatNumber(stats.estimatedUniqueViews?.bySource[source] ?? 0, 'standard', 'decimal', 0) }}
            </td>
          </tr>
          <tr v-if="sources.length > 0">
            <td>Est. media value</td>
            <td v-for="source in sources" :key="source" class="source-table__metric">
              {{ formatMoneyForAdmin(stats.estimatedMediaValue?.bySource[source], { allowZero: true }) }}
            </td>
          </tr>
          <tr v-else>
            <td :colspan="sources.length + 1" class="source-table__empty">No source data for this period</td>
          </tr>
        </DataTable>
      </div>
    </template>
  </FlexComponent>
</template>

<style scoped lang="scss">
.breakdown {
  min-width: 0;

  &__header {
    flex-wrap: wrap;
  }

  &__subtitle {
    margin-top: 2px;
    color: rgba(34, 34, 34, 0.56);
  }
}

.summary-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.summary-metric {
  width: calc(25% - 8px);
  flex-shrink: 0;
}

.source-table-wrap {
  overflow-x: auto;
  min-width: 0;
}

.source-table {
  min-width: max-content;
}

.source-table :deep(th:first-child),
.source-table :deep(td:first-child) {
  width: 160px;
}

.source-table :deep(.source-table__metric) {
  text-align: right;
}

.source-table :deep(.source-table__empty) {
  padding: 32px;
  color: rgba(34, 34, 34, 0.56);
  text-align: center;
}
</style>
